import { useCallback, useEffect, useRef, useState } from 'react';

interface UseMissionScrollParams {
  currentMissionCharacter: number;
  isMobileDevice: boolean;
  hasUserData: boolean;
}

const SCROLL_GAP = 20;

export const useMissionScroll = ({
  currentMissionCharacter,
  isMobileDevice,
  hasUserData,
}: UseMissionScrollParams) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrolledToCharacterRef = useRef<number | null>(null);

  const updateScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > SCROLL_GAP);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1 - SCROLL_GAP);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollButtons();

    container.addEventListener('scroll', updateScrollButtons);

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
    };
  }, [updateScrollButtons]);

  // Auto-scroll to active mission on mobile after data loads
  useEffect(() => {
    if (!(isMobileDevice && hasUserData)) return;

    // Skip if already scrolled to this character
    if (scrolledToCharacterRef.current === currentMissionCharacter) return;

    // Skip character 0 (placeholder or first stage which is already visible)
    if (currentMissionCharacter === 0) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const timeoutId = setTimeout(() => {
      const activeMissionElement = container.querySelector(
        `[data-character="${currentMissionCharacter}"]`
      ) as HTMLElement;

      if (!activeMissionElement) return;

      const containerRect = container.getBoundingClientRect();
      const elementRect = activeMissionElement.getBoundingClientRect();
      const scrollOffset =
        elementRect.left -
        containerRect.left +
        container.scrollLeft -
        containerRect.width / 2 +
        elementRect.width / 2;

      container.scrollTo({
        left: scrollOffset,
        behavior: 'smooth',
      });

      scrolledToCharacterRef.current = currentMissionCharacter;
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [isMobileDevice, hasUserData, currentMissionCharacter]);

  const handleScrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({
      left: -350,
      behavior: 'smooth',
    });
  }, []);

  const handleScrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({
      left: 350,
      behavior: 'smooth',
    });
  }, []);

  return {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    handleScrollLeft,
    handleScrollRight,
  };
};
